import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { AxiosResponse } from 'axios';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import GenericDialog from 'components/GenericDialog';
import InspectorSelector from 'components/InspectorSelector';
import RevisionRowData from 'components/RevisionRowData';
import Spinner from 'components/Spinner';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import { TypographyLocale } from 'components/TypographyLocale';
import UseAuthContext from 'context/AuthContext';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { GetUserOrganization } from 'services/auth-service';
import { assignInspectorToRevision } from 'services/inspections-service';
import { getProjectById } from 'services/project-service';
import { getFeatures, unlockRevision } from 'services/revisions';
import { axiosInstance } from 'utils/AxiosInstance';
import { API_PATHS, USER_ROLES } from 'utils/constants';
import { IErrorResponse, Order, RevisionStatus } from 'utils/contracts';
import { version_options } from 'utils/Formatter';
import { fireSuccessConfetti } from 'utils/successConfetti';
import { getComparator, getErrorHtmlContent, stableSort } from 'utils/utils';
let DEFAULT_ORDER = 'desc',
  DEFAULT_ORDER_BY = 'index';


const RevisionList = ({ data, isLoading, refetch }) => {
  const { projectId } = useParams();
  const { t } = useTranslation();
  const { organizations, setOrganization } = useContext(UseAuthContext);
  const navigate = useNavigate();
  const { role: authorization, organizationId } = useContext(UseAuthContext);
  const [page, setPage] = React.useState(0);
  const [isUnlockLoading, setIsUnlockLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [open, setOpen] = React.useState(false);
  const [openCredit, setOpenCredit] = React.useState(false);
  const [openShareInspector, setOpenShareInspector] = React.useState(false);
  const [shareEmail, setShareEmail] = React.useState('');
  const [selectedOrganization, setSelectedOrganization] = React.useState<any>(null);
  const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);
  const [openDeletePopUp, setOpenDeletePopUp] = React.useState(false);
  const [deleteRevisionID, setDeleteRevisionID] = React.useState('');
  const [order, setOrder] = React.useState<Order>(DEFAULT_ORDER as Order);
  const [orderBy, setOrderBy] = React.useState<string>(DEFAULT_ORDER_BY);
  const [selectedRevision, setSelectedRevision] = React.useState<any>();
  const [revisionData, setRevisionData] = React.useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const {
    data: projectData,
    isLoading: isProjectLoading,
  } = useQuery(
    ['GetProjectByID', projectId],
    async () => await getProjectById(projectId ?? '')
  );
  const { data: organizationData, isLoading: isOrganizationLoading } = useQuery(
    ['getOrganizationById', organizationId],
    async () => await GetUserOrganization(organizationId),
    {
      onSuccess: (data) => {
        setOrganization(data);
      }
    }
  );
  const { data: features, isLoading: isFeaturesLoading } = useQuery('features', getFeatures);


  const threeDViewerAllowedStartDate = (revisionData.some((revision) => new Date(revision.updatedAt) > new Date(features?.threeDViewerStartDate)))
  const threeDViewerAllowedProject = features?.threeDViewerAllowed && ( projectData?.version == version_options[1]?.value || projectData?.version == version_options[2]?.value )

  const isInspectorProjectVersion =
    projectData?.version === version_options[1]?.value || // v3
    projectData?.version === version_options[2]?.value; // v4
  const inspectorAllowed = features?.inspectorAllowed && isInspectorProjectVersion;

  // Users should see the column when any revision is assigned to / inspected by an inspector
  const hasAnyInspectorAssignment = revisionData.some(
    (revision) => revision.inspection?.hasInspectorAssignment
  );
  const showInspectionStatusColumn =
    inspectorAllowed ||
    authorization === USER_ROLES.ADMIN ||
    hasAnyInspectorAssignment;

  const deleteRevisionMutation = useMutation({
    mutationFn: async (revisionID: string) => {
      const response = await axiosInstance.delete<any, AxiosResponse<any>>(
        `${API_PATHS.DELETE_REVISION}/${revisionID}`
      );
      return response;
    },
    onSuccess() {
      refetch();
      setDeleteRevisionID('');
      enqueueSnackbar(t('DELETE_REVISION_MESSAGE_SUCCESS'), {
        variant: 'success',
      });
    },
    onError(error: IErrorResponse, variables, context) {
      if (error) {
        enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
        console.log('onError', error, variables, context);
      }
    },
  });

  const deleteRevision = async (e: any, revisionID: any) => {
    try {
      await deleteRevisionMutation.mutateAsync(revisionID);
      setOpenDeletePopUp(false);
    } catch (error: any) {
      console.log('onError', error);
    }
  };

  useEffect(() => {
    if (data) {
      let rowsOnMount = stableSort(
        data,
        getComparator(DEFAULT_ORDER as Order, DEFAULT_ORDER_BY)
      );
      setRevisionData(rowsOnMount);
    }
  }, [data]);


  const handleRequestSort = React.useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: string) => {
      const isAsc = orderBy === newOrderBy && order === 'asc';
      const toggledOrder = isAsc ? 'desc' : 'asc';
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);

      const sortedRows = stableSort(
        data,
        getComparator(toggledOrder, newOrderBy)
      );
      setRevisionData(sortedRows);
    },
    [order, orderBy, page, rowsPerPage, revisionData]
  );

  const createSortHandler =
    (newOrderBy: string) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, newOrderBy);
    };

  const handleClickOpen = (selectedRevision: any) => {
    setSelectedRevision(selectedRevision);
    setOpen(true);
  };

  // Expose to child menu via window hook
  (window as any).openShareInspector = (revisionId: string) => {
    setSelectedRevision({ id: revisionId });
    setOpenShareInspector(true);
  }

  const topUp = (e: any) => {
    e.preventDefault();

    if (authorization === USER_ROLES.ADMIN) {
      navigate(`/edit/business/${organizationId}`);
    } else {
      navigate('/pricing');
    }
  };

  const handleClickCreditPopUpOpen = () => {
    setOpenCredit(true);
  };

  const handleClickDeletePopUpOpen = (revisionID: any) => {
    setOpenDeletePopUp(true);
    setDeleteRevisionID(revisionID);
  };

  const handleClose = () => {
    setOpen(false);
    setOpenCredit(false);
    setOpenDeletePopUp(false);
    setDeleteRevisionID('');
    setOpenShareInspector(false);
    setShareEmail('');
    setSelectedOrganization(null);
    setIsConfirmed(false);
  };


  if (isProjectLoading || isLoading || isOrganizationLoading || !organizationId) {
    return <Spinner></Spinner>;
  }


  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUnlockRevision = async () => {
    try {
      setIsUnlockLoading(true);
      const result = await unlockRevision(
        selectedRevision?.id,
        projectId,
        organizationData?.id
      );

      if (result.status === 200) {
        fireSuccessConfetti();
        setIsUnlockLoading(false);
        queryClient.invalidateQueries(['getOrganizationById', organizationId]);
        refetch();
      }
      setOpen(false);
    } catch (error: any) {
      enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
      setIsUnlockLoading(false);
      console.log('onError', error);
      setOpen(false);
    }
  };



  return (
    <>
      {
        data.length < 1 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <img
              src={require("../../assets/NoProject.svg").default}
              style={{ marginBottom: "48px" }}

              alt=""
            />
            <TypographyLocale variant="h2">
              {t("NO_REVISION_HEADING_1")}
            </TypographyLocale>
            {/*   <TypographyLocale variant="h3" fontSize="18px">
              {t("NO_REVISION_HEADING_2")}
            </TypographyLocale> */}
          </Box>
        ) : (
          <TableContainer>

            <Table size="small" >
              <TableHead>
                <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
                  <CustomTableFontCell />
                  <CustomTableFontCell
                    sortDirection={orderBy === 'index' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'index'}
                      direction={orderBy === 'index' ? order : 'asc'}
                      onClick={createSortHandler('index')}
                    >
                      {t('REVISION_INDEX_COLUMN')}
                      {orderBy === 'index' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                  <CustomTableFontCell
                    sortDirection={orderBy === 'file_name' ? order : false}
                  >
                    <StyledTableSortLabel
                    
                      active={orderBy === 'file_name'}
                      direction={orderBy === 'file_name' ? order : 'asc'}
                      onClick={createSortHandler('file_name')}
                    >
                      {t('REVISION_FILE_NAME_COLUMN')}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>

                  <CustomTableFontCell
                    sortDirection={orderBy === 'createdBy' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'createdBy'}
                      direction={orderBy === 'createdBy' ? order : 'asc'}
                      onClick={createSortHandler('createdBy')}
                    >
                      {t('REVISION_CREATED_BY_COLUMN')}
                      {orderBy === 'createdBy' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                  <CustomTableFontCell
                    sortDirection={orderBy === 'id' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={createSortHandler('id')}
                    >
                      {t('REVISION_REVISION_ID_COLUMN')}
                      {orderBy === 'id' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>
                  {/* Admin, inspector-enabled projects (v3/v4), or any revision inspected by an inspector */}
                  {showInspectionStatusColumn && <CustomTableFontCell
                  width="90px"
                   /*  sx={{ minWidth: threeDViewerAllowedStartDate ? '90px' : '', maxWidth: threeDViewerAllowedStartDate ? '90px' : ' ' }} */
                    sortDirection={orderBy === 'inspection_status' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'inspection_status'}
                      direction={orderBy === 'inspection_status' ? order : 'asc'}
                      onClick={createSortHandler('inspection_status')}
                    >
                      {t('REVISION_INSPECTION_STATUS_COLUMN')}
                      {orderBy === 'inspection_status' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>}
                  <CustomTableFontCell
                  width="90px"
                    sortDirection={orderBy === 'updatedAt' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'updatedAt'}
                      direction={orderBy === 'updatedAt' ? order : 'asc'}
                      onClick={createSortHandler('updatedAt')}
                    >
                      {t('REVISION_UPDATED_DATE')}
                      {orderBy === 'updatedAt' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </CustomTableFontCell>

                  <TableCell
                    sx={{ maxWidth: '40px' }}
                    sortDirection={orderBy === 'report_1045_pass' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'report_1045_pass'}
                      direction={orderBy === 'report_1045_pass' ? order : 'asc'}
                      onClick={createSortHandler('report_1045_pass')}
                    >
                      {t('REVISION_RATING_GRADE_COLUMN')}
                      {orderBy === 'report_1045_pass' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>


                  </TableCell>

                  {projectData.type == "Residential" && <TableCell
                    sx={{ maxWidth: '40px' }}
                    sortDirection={orderBy === 'h_report_pass' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'h_report_pass'}
                      direction={orderBy === 'h_report_pass' ? order : 'asc'}
                      onClick={createSortHandler('h_report_pass')}
                    >
                      {t('REVISION_H_REPORT_COLUMN')}
                      {orderBy === 'h_report_pass' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </TableCell>}
                  <TableCell
                    sx={{ maxWidth: '40px' }}
                    sortDirection={orderBy === 'weighed_grade' ? order : false}
                  >
                    <StyledTableSortLabel
                      active={orderBy === 'weighed_grade'}
                      direction={orderBy === 'weighed_grade' ? order : 'asc'}
                      onClick={createSortHandler('weighed_grade')}
                    >
                      {t('REVISION_NOTES_GRADE')}
                      {orderBy === 'weighed_grade' ? (
                        <Box component='span' sx={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </StyledTableSortLabel>
                  </TableCell>
                  <CustomTableFontCell style={{ paddingTop: '7px' }} sx={{ textAlign: "center" }}>{t('REVISION_NOTES_RATING')}</CustomTableFontCell>
                  <CustomTableFontCell  sx={{ maxWidth:threeDViewerAllowedStartDate && '60px'}} ></CustomTableFontCell>
                  <CustomTableFontCell  sx={{ maxWidth: threeDViewerAllowedStartDate && '170px' }} ></CustomTableFontCell>
                  {threeDViewerAllowedProject && threeDViewerAllowedStartDate && <CustomTableFontCell ></CustomTableFontCell>}
                </TableRow>
              </TableHead>
              <TableBody >
                {revisionData
                  .map((data: any) => {
                    // Calculate isInspectorAllowedStartDate for each revision (v3 and v4)
                    const isInspectorAllowedStartDate =
                      features?.inspectorAllowedStartDate &&
                      isInspectorProjectVersion &&
                      data.status === RevisionStatus.SUCCESS &&
                      data.startedAt &&
                      new Date(data.startedAt) >= new Date(features?.inspectorAllowedStartDate);
                    const hasInspectorAssignment = Boolean(
                      data.inspection?.hasInspectorAssignment
                    );

                    return (
                      <RevisionRowData
                        props={data}
                        key={data.id}
                        index={data.index}
                        projectId={projectId}
                        refetchData={refetch}
                        handleClickOpen={handleClickOpen}
                        totalPoints={
                          organizationData?.totalCredits ??
                          organizations?.totalCredits ??
                          0
                        }
                        OrgId={organizationData?.id}
                        hasTrial={organizationData?.hasTrial}
                        projectType={projectData.type}
                        projectVersion={projectData?.version}
                        hasProjectThreeDViewerAllowed={threeDViewerAllowedProject}
                        has3DStartDateAllowed={features?.threeDViewerStartDate}
                        isInspectorAllowedStartDate={isInspectorAllowedStartDate}
                        isInspectorAllowed={inspectorAllowed}
                        showInspectionStatusColumn={showInspectionStatusColumn}
                        hasInspectorAssignment={hasInspectorAssignment}
                        handleClickCreditPopUpOpen={handleClickCreditPopUpOpen}
                        handleClickDeletePopUpOpen={handleClickDeletePopUpOpen}
                      />
                    );
                  })}
              </TableBody>
            </Table>
            {/*  <TablePagination
                sx={{ width: "100%" }}
                rowsPerPageOptions={[5, 10, 25]}
                component='div'
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('MUI_ROWS_PER_PAGE')}
                labelDisplayedRows={({ from, to, count }) => t('MUI_PAGINATION_RANGE', { from, to, count })}
                getItemAriaLabel={(type) => t(`MUI_PAGINATION_${type.toUpperCase()}_PAGE`)}
              /> */}
            <GenericDialog
              open={open}
              onClose={handleClose}
              title={t('REVISION_UNLOCK_TITLE')}
              onConfirm={handleUnlockRevision}
              confirmButtonText={t('REVISION_UNLOCK_YES')}
              cancelButtonText={t('REVISION_UNLOCK_NO')}
              isLoading={isUnlockLoading}
            >
              {t('REVISION_UNLOCK_DESCRIPTION').replace(
                '$$1',
                Number.isFinite(Number(selectedRevision?.requiredPoints))
                  ? String(selectedRevision.requiredPoints)
                  : '—'
              )}
            </GenericDialog>
            <GenericDialog
              open={openCredit}
              onClose={handleClose}
              title={t('REVISION_CREDIT_ADD_TITLE')}
              onConfirm={topUp}
              confirmButtonText={t('REVISION_CREDIT_ADD_YES')}
              cancelButtonText={t('REVISION_CREDIT_ADD_NO')}
            >
              {t('REVISION_CREDIT_ADD_DESCRIPTION')}
            </GenericDialog>
            <GenericDialog
              open={openDeletePopUp}
              onClose={handleClose}
              isLoading={deleteRevisionMutation.isLoading}
              title={t('REVISION_DELETE_POPUP_TITLE')}
              onConfirm={() => deleteRevision(null, deleteRevisionID)}
              confirmButtonText={t('DELETE_BUTTON')}
              cancelButtonText={t('CANCEL_BUTTON')}
            >
              {t('REVISION_DELETE_POPUP_DESCRIPTION')}
            </GenericDialog>
            <GenericDialog
              open={openShareInspector}
              onClose={handleClose}
              titleAlign="flex-start"

              title={`${t('INSPECTOR_SELECTOR_ASSIGN_REVISION')} ${selectedRevision?.id}`}
              subtitle={t('LABORATORY_SELECTOR_ONLY_ONE_LABORATORY')}
              maxWidth={false}
              fullWidth={false}
              paperSx={{
                width: '540px',
                marginTop: '5vh'
              }}
              onConfirm={async () => {
                try {
                  if (!selectedOrganization) {
                    enqueueSnackbar(t('INSPECTOR_SELECTOR_PLEASE_SELECT_LABORATORY'), { variant: 'error' });
                    return;
                  }

                  if (!isConfirmed) {
                    enqueueSnackbar(t('INSPECTOR_SELECTOR_PLEASE_CONFIRM_ASSIGNMENT'), { variant: 'error' });
                    return;
                  }

                  const assignment = {
                    targetOrganizationId: selectedOrganization.id,
                    assignmentType: 'organization' as const
                  };

                  const res = await assignInspectorToRevision(selectedRevision?.id, projectId, organizationData?.id, assignment!)

                  enqueueSnackbar(t('LABORATORY_ASSIGNED_SUCCESSFULLY'), { variant: 'success' });
                  refetch(); // Refresh the data to show the new inspection status
                  handleClose();
                } catch (error) {
                  enqueueSnackbar(getErrorHtmlContent(error), { variant: 'error' });
                }
              }}
              confirmButtonText={t('ASSIGN_BUTTON')}
              cancelButtonText={t('CANCEL_BUTTON')}
            >
              <Box py={2} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%' }}>
                <TypographyLocale variant="h3" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('INSPECTOR_SELECTOR_LABEL')}
                </TypographyLocale>
                <InspectorSelector
                  selectedOrganization={selectedOrganization}
                  onOrganizationChange={setSelectedOrganization}
                  isConfirmed={isConfirmed}
                  onConfirmationChange={setIsConfirmed}
                />
              </Box>
            </GenericDialog>
          </TableContainer>
        )
      }
    </>
  );
};

export default RevisionList;
